import type { Ref } from '@vue/composition-api';
import { computed, ref, watch } from '@vue/composition-api';
import type { Transaction } from 'prosemirror-state';
import { useTextSelection, useDebounceFn } from '@vueuse/core';
import type { Editor as TipTapEditor } from '@tiptap/vue-2';

export function useCreateContextualCommentButton(editorRef: Ref<HTMLElement>) {
  // Native eventlistener needed as Prosemirror doesn't fire a
  // selection change event when the selection is cleared
  const selection = useTextSelection();

  const buttonPosition: Ref<{ top: number; left: number }> = ref(null);
  const selectionLength = computed(() => selection.text?.value?.length);
  const isSelecting = ref(false);

  watch(selectionLength, length => {
    isSelecting.value = length > 0;
    debouncedResetIsSelecting();
  });

  const debouncedResetIsSelecting = useDebounceFn(() => (isSelecting.value = false), 300);

  const onSelectionUpdate = ({ editor }: { editor: TipTapEditor; transaction: Transaction }) => {
    if (!selectionLength.value) {
      buttonPosition.value = null;
      return;
    }

    const { to } = editor.view.state.selection;
    const { left, bottom } = editor.view.coordsAtPos(to);
    const editorOffset = editorRef.value.getBoundingClientRect();

    buttonPosition.value = {
      top: bottom - editorOffset.top - 60,
      left: left - editorOffset.left,
    };
  };

  const onCreateComment = () => (buttonPosition.value = null);

  return {
    buttonPosition,
    isSelecting,
    onSelectionUpdate,
    onCreateComment,
  };
}
